import { useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { Check, Sparkles } from 'lucide-react'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Popover, PopoverContent, PopoverAnchor } from '@/components/ui/popover'
import { Select, SelectContent, SelectTrigger } from '@/components/ui/select'
import { Typography } from '@/components/ui/typography'
import { showError } from '@/lib/toast'
import {
  useDealDecisionOptions,
  useRecordDealDecision,
} from '@/hooks/use-deal-leads'
import {
  DispositionSelectItems,
  RecommendationSummary,
  ReviewOptionContent,
} from '@/components/custom/deal-review/review-controls'
import {
  quietToneClasses,
  recordedFillClasses,
  suggestedToneClasses,
} from '@/features/deal-review/review-tones'
import {
  getLeadDispositionOption,
  saveableDispositionOrNull,
} from '@/features/deal-review/review-options'
import { displayNameFor } from '@/features/deal-review/deal-lead-views'
import { dispositionLabel } from '@/features/deal-review/deal-lead-labels'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import type { DealLeadDetail, DealLeadListItem, FieldOption } from '@/types/api'

// Nothing is written until a reviewer picks: a rendered row must never record the model's opinion.

function shownDisposition(lead: DealLeadListItem) {
  return lead.disposition === 'UNREVIEWED'
    ? (lead.recommendedDisposition ?? 'UNREVIEWED')
    : lead.disposition
}

// Validated against loaded options: DealCloud can retire a reason a lead still names.
function knownReason(
  lead: DealLeadListItem,
  reasons: readonly FieldOption[]
): string | null {
  const candidates = [lead.dispositionReason, lead.recommendedPassReason]
  const known = candidates.find(
    (id) => id !== undefined && reasons.some((reason) => reason.value === id)
  )
  return known ?? null
}

function SuggestionTooltip({
  lead,
  enabled,
  children,
}: {
  lead: DealLeadListItem
  enabled: boolean
  children: ReactNode
}) {
  if (!enabled) return children

  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent className="max-w-72">
        <RecommendationSummary
          summary={`Screening suggests ${dispositionLabel(
            lead.recommendedDisposition ?? 'UNREVIEWED'
          )}`}
          reason={lead.recommendationReason}
          footnote="Nothing is recorded until you choose."
        />
      </TooltipContent>
    </Tooltip>
  )
}

export function DecisionSelect({
  lead,
  onDecided,
}: {
  lead: DealLeadListItem
  onDecided?: (detail: DealLeadDetail) => void
}) {
  const save = useRecordDealDecision(lead.id)
  const options = useDealDecisionOptions()

  const [awaitingReason, setAwaitingReason] = useState<string | null>(null)
  const anchorRef = useRef<HTMLDivElement>(null)

  const shown = shownDisposition(lead)
  const option = getLeadDispositionOption(shown)
  const reasons = options.data?.passReasons ?? []

  const suggested = lead.disposition === 'UNREVIEWED' && shown !== 'UNREVIEWED'
  const quiet = quietToneClasses[option.tone]
  const proposal = suggestedToneClasses[option.tone]

  function write(disposition: string, reason: string | null) {
    const next = saveableDispositionOrNull(disposition)
    if (next === null) return

    save.mutate(
      {
        version: lead.version,
        disposition: next,
        reason,
        addOnConfirmed: lead.addOnConfirmed ?? null,
      },
      {
        onSuccess: onDecided,
        onError: (error) =>
          showError(error, 'That decision could not be saved'),
      }
    )
  }

  return (
    <Popover
      open={awaitingReason !== null}
      onOpenChange={(open) => {
        if (!open) setAwaitingReason(null)
      }}
    >
      {/* Radix Select renders no DOM node of its own, so the popover needs a real anchor. */}
      <PopoverAnchor asChild>
        <div ref={anchorRef} className="min-w-0">
          <Select
            // Only the reviewer's decision: a Select seeded with the proposal can't fire on confirming it.
            value={
              lead.disposition === 'UNREVIEWED' ? undefined : lead.disposition
            }
            disabled={!lead.capabilities.canDecide || save.isPending}
            onValueChange={(value) => {
              const next = saveableDispositionOrNull(value)
              if (next === null) return

              const needsReason = (
                options.data?.dispositionsRequiringReason ?? []
              ).includes(next)

              if (needsReason) {
                // Deferred: the closing Select's pointer events would dismiss a popover opened this tick.
                setTimeout(() => setAwaitingReason(next), 0)
                return
              }

              write(next, null)
            }}
          >
            <SuggestionTooltip lead={lead} enabled={suggested}>
              <SelectTrigger
                aria-label={
                  suggested
                    ? `Decision for ${displayNameFor(lead) ?? 'this lead'} — none recorded, screening suggests ${dispositionLabel(lead.recommendedDisposition ?? 'UNREVIEWED')}`
                    : `Decision for ${displayNameFor(lead) ?? 'this lead'}`
                }
                className={`h-auto min-h-6 w-auto min-w-0 rounded-full bg-transparent px-2 py-0.75 [&>svg]:size-2.75 [&>svg:last-child]:ml-1 [&>svg:last-child]:opacity-60 ${
                  suggested
                    ? proposal.trigger
                    : `${quiet.trigger} ${recordedFillClasses[option.tone]}`
                }`}
              >
                {suggested ? (
                  <Sparkles
                    aria-hidden="true"
                    className={`mr-1.25 !size-2.75 shrink-0 ${proposal.icon}`}
                  />
                ) : (
                  <span
                    aria-hidden="true"
                    className={`mr-1.25 size-1.5 shrink-0 rounded-full ${quiet.dot}`}
                  />
                )}
                {/* SelectValue shows the placeholder while the Select is empty, so render the option directly. */}
                <ReviewOptionContent option={option} compact showIcon={false} />
              </SelectTrigger>
            </SuggestionTooltip>
            <SelectContent>
              <DispositionSelectItems />
            </SelectContent>
          </Select>
        </div>
      </PopoverAnchor>
      <PopoverContent
        align="start"
        className="w-72 p-0"
        // Radix Select hands focus back to its trigger on a timer; that focusin must not dismiss the picker.
        onFocusOutside={(event) => {
          if (
            event.target instanceof Node &&
            anchorRef.current?.contains(event.target)
          )
            event.preventDefault()
        }}
      >
        <ReasonPicker
          reasons={reasons}
          preselected={knownReason(lead, reasons)}
          recommended={lead.recommendedPassReason ?? null}
          onPick={(reason) => {
            const disposition = awaitingReason
            setAwaitingReason(null)
            if (disposition !== null) write(disposition, reason)
          }}
        />
      </PopoverContent>
    </Popover>
  )
}

function ReasonPicker({
  reasons,
  preselected,
  recommended,
  onPick,
}: {
  reasons: readonly FieldOption[]
  preselected: string | null
  recommended: string | null
  onPick: (reason: string) => void
}) {
  const ordered = [...reasons].sort((a, b) => {
    if (a.value === preselected) return -1
    if (b.value === preselected) return 1
    return 0
  })

  return (
    <Command>
      <div className="border-b border-primary/5 px-3 py-2">
        <Typography as="span" variant="small" className="text-primary/60">
          Reason for passing
        </Typography>
      </div>
      <CommandInput placeholder="Search reasons…" />
      <CommandList>
        <CommandEmpty>No matching reason.</CommandEmpty>
        <CommandGroup>
          {ordered.map((reason) => (
            <CommandItem
              key={reason.value}
              value={reason.label}
              onSelect={() => onPick(reason.value)}
            >
              <Check
                aria-hidden="true"
                className={`size-3.5 ${
                  reason.value === preselected ? 'opacity-100' : 'opacity-0'
                }`}
              />
              <span className="truncate">{reason.label}</span>
              {reason.value === recommended && (
                <Sparkles
                  aria-label="Screening found this"
                  className="ml-auto size-3.5 shrink-0 text-secondary"
                />
              )}
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </Command>
  )
}
